import z from "zod";
import { ChatAgent } from "./enums/ChatAgent";
import {
  ChangeRequestIdScheme,
  ChatSessionIdScheme,
  ChatSessionMessageIdScheme,
} from "./ids";
import { ChatMessageRole } from "./enums/ChatMessageRole";

export const ChatSessionDBScheme = z.object({
  id: ChatSessionIdScheme,
  name: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  sessionId: z.string().optional(),
  changeRequestId: ChangeRequestIdScheme.optional(),
  activeAgent: z.enum(ChatAgent).optional(),
});

export type ChatSession = z.infer<typeof ChatSessionDBScheme>;

export const ChatMessageAttachmentScheme = z.object({
  url: z.string(),
  filename: z.string(),
  contentType: z.string(),
});

export type ChatMessageAttachment = z.infer<typeof ChatMessageAttachmentScheme>;

export const ChatSessionMessageDBScheme = z.object({
  id: ChatSessionMessageIdScheme,
  role: z.enum(ChatMessageRole),
  content: z.string(),
  attachments: z.array(ChatMessageAttachmentScheme).optional(),
  createdAt: z.iso.datetime(),
});

export type ChatSessionMessage = z.infer<typeof ChatSessionMessageDBScheme>;

export const AgentProgressPhaseScheme = z.enum(["started", "done"]);

export type AgentProgressPhase = z.infer<typeof AgentProgressPhaseScheme>;

export const AgentProgressEventScheme = z.object({
  tool: z.string(),
  phase: AgentProgressPhaseScheme,
});

export type AgentProgressEvent = z.infer<typeof AgentProgressEventScheme>;

export const AgentStreamUserMessageEventScheme = z.object({
  type: z.literal("user_message"),
  message: ChatSessionMessageDBScheme,
});

export const AgentStreamProgressEventScheme = z.object({
  type: z.literal("progress"),
  tool: z.string(),
  phase: AgentProgressPhaseScheme,
});

export const AgentStreamAssistantMessageEventScheme = z.object({
  type: z.literal("assistant_message"),
  message: ChatSessionMessageDBScheme,
});

export const AgentStreamSessionEventScheme = z.object({
  type: z.literal("session"),
  session: ChatSessionDBScheme,
});

export const AgentStreamErrorEventScheme = z.object({
  type: z.literal("error"),
  message: z.string(),
});

export const AgentStreamDoneEventScheme = z.object({
  type: z.literal("done"),
});

export const AgentStreamEventScheme = z.discriminatedUnion("type", [
  AgentStreamUserMessageEventScheme,
  AgentStreamProgressEventScheme,
  AgentStreamAssistantMessageEventScheme,
  AgentStreamSessionEventScheme,
  AgentStreamErrorEventScheme,
  AgentStreamDoneEventScheme,
]);

export type AgentStreamEvent = z.infer<typeof AgentStreamEventScheme>;
